import React, { useEffect, useRef, useState } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import {
  InlineVideoPlayer,
  InlineVideoPlayerHandle,
} from "@/components/ui/InlineVideoPlayer";
import { UnifiedAuthenticatedImage } from "@/components/ui/UnifiedAuthenticatedImage";
import { PlayIcon } from "@/components/icons";
import { CarouselTapZones } from "./CarouselTapZones";

interface CarouselVideoSlideProps {
  mediaItem: MediaItem;
  getStreamUrlByFileId?: (fileId: string) => string | undefined;
  onPrev: () => void;
  onNext: () => void;
  /** Multi-item carousel — left/right edges navigate, wide center pauses. */
  showTapZones?: boolean;
  objectFit?: "cover" | "contain";
  /**
   * Lazy: start as a thumbnail and only mount the video on the first center
   * tap (no autoplay). Used in the posts grid, where many cards would
   * otherwise autoplay at once. Default false = autoplay inline (review feed).
   */
  lazy?: boolean;
}

const PlayGlyph: React.FC = () => (
  <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black/50">
      <PlayIcon className="ml-1 h-8 w-8 text-white" />
    </div>
  </div>
);

/**
 * A video slide with Instagram-style tap controls: the native scrubber is
 * hidden, a wide center band toggles play/pause, and the narrow edges navigate
 * the carousel. In autoplay mode (review feed) it plays on mount; in lazy mode
 * (posts grid) it shows a thumbnail with a play glyph until the first center
 * tap. A play glyph marks the paused/not-started state.
 */
export const CarouselVideoSlide: React.FC<CarouselVideoSlideProps> = ({
  mediaItem,
  getStreamUrlByFileId,
  onPrev,
  onNext,
  showTapZones = true,
  objectFit = "cover",
  lazy = false,
}) => {
  const playerRef = useRef<InlineVideoPlayerHandle>(null);
  const [paused, setPaused] = useState(false);
  // Lazy slides start as a thumbnail; the first center tap mounts the player.
  const [started, setStarted] = useState(!lazy);

  // A new slide reverts a lazy player back to its thumbnail.
  useEffect(() => {
    setStarted(!lazy);
  }, [mediaItem.id, lazy]);

  const centerTap = () => {
    if (!started) {
      setStarted(true);
      return;
    }
    playerRef.current?.togglePlay();
  };

  const overlayTapSurface = showTapZones ? (
    <CarouselTapZones
      mode="video"
      onPrev={onPrev}
      onNext={onNext}
      onToggle={centerTap}
    />
  ) : (
    // Single video (no carousel): the whole surface toggles play/pause.
    <div
      className="absolute inset-0 z-20 cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        centerTap();
      }}
      aria-hidden="true"
    />
  );

  if (!started) {
    return (
      <div className="relative h-full w-full">
        <UnifiedAuthenticatedImage
          mediaItem={mediaItem}
          alt={mediaItem.getName()}
          useThumbnail
          className="h-full w-full object-cover"
        />
        <PlayGlyph />
        {overlayTapSurface}
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <InlineVideoPlayer
        ref={playerRef}
        mediaItem={mediaItem}
        autoPlay
        loop
        muted
        controls={false}
        objectFit={objectFit}
        getStreamUrlByFileId={getStreamUrlByFileId}
        onPlayStateChange={setPaused}
        className="h-full w-full"
      />
      {overlayTapSurface}
      {paused && <PlayGlyph />}
    </div>
  );
};
